import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authApi } from '@api/endpoints/auth'
import { useAuthStore } from '@store/authStore'
import {
  LoginRequest,
  MfaVerificationRequest,
  StartPhoneVerificationRequest,
  VerifyPhoneRequest,
  CompleteRegistrationRequest,
} from '@api/types/auth'

// Validate token query
export function useValidateToken() {
  return useQuery({
    queryKey: ['validateToken'],
    queryFn: authApi.validateToken,
    retry: false,
    enabled: !!localStorage.getItem('adventurecore_token'),
  })
}

// Login mutation
export function useLogin() {
  const { login: setAuthState, setMfaRequired } = useAuthStore()

  return useMutation({
    mutationFn: (credentials: LoginRequest) => authApi.login(credentials),
    onSuccess: response => {
      if (response.success && response.token && response.user && response.refreshToken && response.expiresAt && response.refreshTokenExpiresAt) {
        // Normal login successful
        setAuthState(response.token, response.refreshToken, response.user, response.expiresAt, response.refreshTokenExpiresAt)
      } else if (response.mfaRequired && response.mfaToken) {
        // MFA required
        setMfaRequired(true, response.mfaToken)
      }
    },
  })
}

// MFA verification mutation
export function useVerifyMfa() {
  const { login: setAuthState } = useAuthStore()

  return useMutation({
    mutationFn: (data: MfaVerificationRequest) => authApi.verifyMfa(data),
    onSuccess: response => {
      if (response.success && response.token && response.user && response.refreshToken && response.expiresAt && response.refreshTokenExpiresAt) {
        setAuthState(response.token, response.refreshToken, response.user, response.expiresAt, response.refreshTokenExpiresAt)
      }
    },
  })
}

// Logout mutation
export function useLogout() {
  const queryClient = useQueryClient()
  const { logout: clearAuthState } = useAuthStore()

  return useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      clearAuthState()
      queryClient.clear()
      window.location.href = '/login'
    },
  })
}

// Change password mutation
export function useChangePassword() {
  return useMutation({
    mutationFn: ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) =>
      authApi.changePassword(currentPassword, newPassword),
  })
}

// Phone Verification Registration Flow

// Step 1: Start phone verification
export function useStartPhoneVerification() {
  return useMutation({
    mutationFn: (data: StartPhoneVerificationRequest) => authApi.startPhoneVerification(data),
  })
}

// Step 2: Verify phone code
export function useVerifyPhone() {
  return useMutation({
    mutationFn: (data: VerifyPhoneRequest) => authApi.verifyPhone(data),
  })
}

// Step 3: Complete registration
export function useCompleteRegistration() {
  const { login: setAuthState } = useAuthStore()

  return useMutation({
    mutationFn: (data: CompleteRegistrationRequest) => authApi.completeRegistration(data),
    onSuccess: response => {
      if (response.success && response.token) {
        // Auto-login after successful registration
        // Note: We'll need to fetch user profile or include it in the response
        // For now, we'll just store the token
        localStorage.setItem('adventurecore_token', response.token)
      }
    },
  })
}
